package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.admin.util.LocalViewportMode
import com.merkost.handpass.admin.util.ViewportMode
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.DensityV
import com.merkost.handpass.sharedweb.theme.Dur
import com.merkost.handpass.sharedweb.theme.Ease
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.PadLg
import com.merkost.handpass.sharedweb.theme.RadiusCard
import com.merkost.handpass.sharedweb.theme.RowHover
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

data class AdminColumn<T>(
    val header: String,
    val widthFr: String = "1fr",
    val sortKey: String? = null,
    val cell: @Composable (T) -> Unit,
)

val AdminTableWrapperStyle = CssStyle.base {
    Modifier
        .backgroundColor(Surface.value())
        .styleModifier {
            property("border-radius", "${RadiusCard.value()}")
            property("box-shadow", "var(--hp-shadow-card)")
            property("overflow-x", "auto")
            property("overflow-y", "hidden")
        }
}

val AdminTableWrapperFlushStyle = CssStyle.base {
    Modifier
        .backgroundColor(Surface.value())
        .styleModifier { property("overflow-x", "auto"); property("overflow-y", "hidden") }
}

val AdminTableHeaderStyle = CssStyle.base {
    Modifier
        .backgroundColor(Surface.value())
        .display(DisplayStyle.Grid)
        .borderBottom(1.px, LineStyle.Solid, Border.value())
        .styleModifier {
            property("position", "sticky")
            property("top", "0")
            property("z-index", "1")
        }
}

val AdminTableHeaderCellStyle = CssStyle.base {
    Modifier
        .color(OnSurfaceFaint.value())
        .fontWeight(600)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .styleModifier {
            property("text-transform", "uppercase")
            property("letter-spacing", "0.5px")
        }
}

val AdminTableSortHeaderStyle = CssStyle.base {
    Modifier
        .cursor(Cursor.Pointer)
        .styleModifier {
            property("min-height", "${TouchTarget.value()}")
        }
}

val AdminTableRowStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Grid)
            .alignItems(AlignItems.Center)
            .borderBottom(1.px, LineStyle.Solid, Border.value())
            .transition(Transition.of("background-color", Dur.Fast, Ease.Standard))
    }
    hover {
        Modifier.backgroundColor(RowHover.value())
    }
    cssRule(":last-child") {
        Modifier.styleModifier { property("border-bottom", "none") }
    }
    cssRule(":hover .admin-row-actions") {
        Modifier.opacity(1).styleModifier { property("transform", "translateX(0)") }
    }
}

@Composable
fun <T> AdminTable(
    columns: List<AdminColumn<T>>,
    rows: List<T>,
    rowId: (T) -> String,
    onRowClick: ((T) -> Unit)? = null,
    sortBy: String? = null,
    sortAsc: Boolean = true,
    onSort: ((String) -> Unit)? = null,
    selectable: Boolean = false,
    selectedIds: Set<String> = emptySet(),
    onSelectionChange: ((Set<String>) -> Unit)? = null,
    flush: Boolean = false,
    mobileCard: (@Composable (T) -> Unit)? = null,
) {
    if (mobileCard != null && LocalViewportMode.current == ViewportMode.Mobile) {
        AdminTableMobileList(rows, rowId, onRowClick, selectable, selectedIds, onSelectionChange, mobileCard)
        return
    }
    val gridTemplate = (if (selectable) "40px " else "") + columns.joinToString(" ") { it.widthFr }

    Div(attrs = (if (flush) AdminTableWrapperFlushStyle else AdminTableWrapperStyle).toAttrs()) {
        Div(attrs = Modifier.styleModifier { property("overflow-x", "auto") }.toAttrs()) {
            Div(
                attrs = AdminTableHeaderStyle.toModifier()
                    .styleModifier {
                        property("grid-template-columns", gridTemplate)
                        property("min-width", "fit-content")
                    }
                    .toAttrs {
                        style { padding(11.px, PadLg.value()) }
                    }
            ) {
                if (selectable) {
                    val rowIds = rows.map(rowId).toSet()
                    val allSelected = rowIds.isNotEmpty() && (rowIds - selectedIds).isEmpty()
                    CheckboxInput(checked = allSelected) {
                        attr("aria-label", "Select all rows")
                        onChange {
                            onSelectionChange?.invoke(if (allSelected) selectedIds - rowIds else selectedIds + rowIds)
                        }
                    }
                }
                columns.forEach { col ->
                    val sortKey = col.sortKey
                    val sortCallback = onSort
                    val sortable = sortKey != null && sortCallback != null
                    Span(
                        attrs = AdminTableHeaderCellStyle.toModifier()
                            .then(if (sortable) AdminTableSortHeaderStyle.toModifier() else Modifier)
                            .toAttrs {
                                style { fontSize(WebType.Micro) }
                                if (sortable) {
                                    attr("role", "button")
                                    attr("tabindex", "0")
                                    attr("aria-sort", ariaSort(col.sortKey, sortBy, sortAsc))
                                    onClick { sortCallback(sortKey) }
                                    onKeyDown { event ->
                                        if (event.key == "Enter" || event.key == " ") {
                                            event.preventDefault()
                                            sortCallback(sortKey)
                                        }
                                    }
                                }
                            }
                    ) {
                        Text(col.header + sortIndicator(col.sortKey, sortBy, sortAsc))
                    }
                }
            }
            rows.forEach { row ->
                val id = rowId(row)
                val isSelected = id in selectedIds
                Div(
                    attrs = AdminTableRowStyle.toModifier()
                        .styleModifier {
                            property("grid-template-columns", gridTemplate)
                            property("min-width", "fit-content")
                        }
                        .then(if (onRowClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
                        .then(if (isSelected) Modifier.backgroundColor(SurfaceMuted.value()) else Modifier)
                        .toAttrs {
                            if (onRowClick != null) onClick { onRowClick(row) }
                            style { padding(DensityV.value(), PadLg.value()) }
                        }
                ) {
                    if (selectable) {
                        CheckboxInput(checked = isSelected) {
                            attr("aria-label", "Select row")
                            onChange {
                                onSelectionChange?.invoke(if (isSelected) selectedIds - id else selectedIds + id)
                            }
                            onClick { it.stopPropagation() }
                        }
                    }
                    columns.forEach { it.cell(row) }
                }
            }
        }
    }
}

@Composable
private fun <T> AdminTableMobileList(
    rows: List<T>,
    rowId: (T) -> String,
    onRowClick: ((T) -> Unit)?,
    selectable: Boolean,
    selectedIds: Set<String>,
    onSelectionChange: ((Set<String>) -> Unit)?,
    mobileCard: @Composable (T) -> Unit,
) {
    Div(
        attrs = Modifier.styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "${HandpassSpacing.SM}")
        }.toAttrs()
    ) {
        rows.forEach { row ->
            val id = rowId(row)
            val isSelected = id in selectedIds
            AdminCard(
                paddingScale = AdminCardPadding.Compact,
                onClick = onRowClick?.let { cb -> { cb(row) } },
            ) {
                Div(
                    attrs = Modifier.styleModifier {
                        property("display", "flex")
                        property("align-items", "flex-start")
                        property("gap", "${HandpassSpacing.MD}")
                    }.toAttrs()
                ) {
                    if (selectable) {
                        CheckboxInput(checked = isSelected) {
                            attr("aria-label", "Select row")
                            onChange {
                                onSelectionChange?.invoke(if (isSelected) selectedIds - id else selectedIds + id)
                            }
                            onClick { it.stopPropagation() }
                        }
                    }
                    Div(
                        attrs = Modifier.styleModifier {
                            property("flex", "1")
                            property("min-width", "0")
                        }.toAttrs()
                    ) {
                        mobileCard(row)
                    }
                }
            }
        }
    }
}

private fun sortIndicator(colKey: String?, sortBy: String?, sortAsc: Boolean): String =
    if (colKey != null && colKey == sortBy) (if (sortAsc) " ↑" else " ↓") else ""

private fun ariaSort(colKey: String?, sortBy: String?, sortAsc: Boolean): String =
    if (colKey != null && colKey == sortBy) (if (sortAsc) "ascending" else "descending") else "none"
